import React, { Component } from "react";
import { Map as MapGL, Source, Layer, useControl } from "react-map-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import DrawControl from "./DrawControl";



class Map extends Component {
  constructor(props) {
    super(props);
    this.mapRef = React.createRef();
    this._moveTimeout = null;
    this._zoomTimeout = null;

    this.lastCircleCenter = null;     // This is stored as { lat, lng }
    this.lastCircleFeatureId = null;

    this.state = {
      features: {},
      hoveredPropertyId: null
    };
  }

  fitMapToCircle = (center, miles, { padding = 60, duration = 300, maxZoom = 17 } = {}) => {
    const mapper = this.mapRef?.current?.getMap?.();
    if (!mapper || !center || !Number.isFinite(miles)) return;

    const rMeters = Math.max(0, Number(miles)) * 1609.34;

    const n = this.destinationPoint(center.lat, center.lng, rMeters, 0);
    const e = this.destinationPoint(center.lat, center.lng, rMeters, 90);
    const s = this.destinationPoint(center.lat, center.lng, rMeters, 180);
    const w = this.destinationPoint(center.lat, center.lng, rMeters, 270);

    const minLng = Math.min(w.lng, e.lng);
    const maxLng = Math.max(w.lng, e.lng);
    const minLat = Math.min(s.lat, n.lat);
    const maxLat = Math.max(s.lat, n.lat);

    try {
      mapper.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
        padding,
        duration,
        maxZoom
      });
    } catch { }
  };

  onDelete = e => { };

  componentWillUnmount() {
    clearInterval(this._movetimeout);
    clearInterval(this._zoomtimeout);
  }

  shouldComponentUpdate(prevProps, prevState) {
    if (
      prevProps.map_type !== this.props.map_type ||
      prevProps.dark_mode !== this.props.dark_mode ||
      prevProps.drawing !== this.props.drawing ||
      prevProps.draw_mode !== this.props.draw_mode ||
      prevProps.circle_radius_miles !== this.props.circle_radius_miles ||
      prevProps.selectedIds !== this.props.selectedIds ||
      prevProps.properties_on_map_ids !== this.props.properties_on_map_ids ||
      prevProps.leadData !== this.props.leadData ||
      prevProps.hoveredPropertyId !== this.props.hoveredPropertyId ||
      prevState.hoveredPropertyId !== this.state.hoveredPropertyId ||
      prevProps.active_property !== this.props.active_property
    ) {
      return true;
    }
    return false;
  }

  destinationPoint = (lat, lng, distanceMeters, bearingDeg) => {
    const R = 6378137; // We are using WGS84 system to get the conversion
    const δ = distanceMeters / R;
    const θ = (bearingDeg * Math.PI) / 180;
    const φ1 = (lat * Math.PI) / 180;
    const λ1 = (lng * Math.PI) / 180;

    const sinφ1 = Math.sin(φ1), cosφ1 = Math.cos(φ1);
    const sinδ = Math.sin(δ), cosδ = Math.cos(δ);
    const sinθ = Math.sin(θ), cosθ = Math.cos(θ);

    const sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * cosθ;
    const φ2 = Math.asin(sinφ2);
    const y = sinθ * sinδ * cosφ1;
    const x = cosδ - sinφ1 * sinφ2;
    const λ2 = λ1 + Math.atan2(y, x);

    return { lat: (φ2 * 180) / Math.PI, lng: ((λ2 * 180) / Math.PI + 540) % 360 - 180 };
  };

  makeCircleRing = ({ lat, lng }, rMeters, steps = 64) => {
    const ring = [];
    for (let i = 0; i < steps; i++) {
      const bearing = (360 * i) / steps;
      const p = this.destinationPoint(lat, lng, rMeters, bearing);
      ring.push([p.lng, p.lat]);
    }
    ring.push(ring[0]);
    return ring;
  };

  // This is a backup incase we lost the center. We then recover from the current polygon
  getCurrentCircleCenter = () => {
    try {
      const all = this.drawControl?.getAll();
      const poly = all?.features?.find(f => f.geometry?.type === "Polygon");
      if (!poly) return null;
      const coords = poly.geometry.coordinates?.[0];
      if (!coords || coords.length === 0) return null;
      let sumLat = 0, sumLng = 0, n = coords.length - 1;
      for (let i = 0; i < n; i++) {
        sumLng += coords[i][0];
        sumLat += coords[i][1];
      }
      return { lat: sumLat / n, lng: sumLng / n };
    } catch {
      return null;
    }
  };

  buildCircleAt = (center, miles) => {
    if (!this.drawControl || !center) return;

    const radiusMeters = Math.max(0, Number(miles || 0)) * 1609.34;
    const ring = this.makeCircleRing(center, radiusMeters, 64);
    const feature = {
      type: "Feature",
      properties: {},
      geometry: { type: "Polygon", coordinates: [ring] }
    };

    // We replace existing feature if we have one
    try { if (this.lastCircleFeatureId) this.drawControl.delete(this.lastCircleFeatureId); } catch { }

    const added = this.drawControl.add(feature);
    const newId = Array.isArray(added) ? added[0] : added;

    this.lastCircleCenter = center;
    this.lastCircleFeatureId = newId;

    try { this.drawControl.changeMode("direct_select", { featureId: newId }); } catch { }

    this.props.drawingCreated?.();
    this.sendCoordinates([{ geometry: feature.geometry }]);
  };



  componentDidUpdate(prevProps, prevState) {



    if (prevProps.properties_on_map_ids !== this.props.properties_on_map_ids && this.mapRef?.current) {
      this.handleIdle();
    } else if (prevProps.active_property !== this.props.active_property && this.mapRef?.current) {
      this.handleIdle();
    }

    const radiusChanged = prevProps.circle_radius_miles !== this.props.circle_radius_miles;
    if (
      radiusChanged &&
      this.props.drawing &&
      this.props.draw_mode === "circle" &&
      this.drawControl
    ) {
      // We prefer the last clicked center. But sometimes there would be instances where we cannot get that or it behaved weirdly. In this case we recover from current polygon
      const center =
        this.lastCircleCenter ||
        this.getCurrentCircleCenter();

      if (center) {
        this.buildCircleAt(center, this.props.circle_radius_miles);
        this.fitMapToCircle(center, this.props.circle_radius_miles);
      }
    }



    if (prevProps.hoveredPropertyId !== this.props.hoveredPropertyId) {
      this.setState({ hoveredPropertyId: this.props.hoveredPropertyId });
    }
  }

  sendCoordinates(features) {
    let drawing_coordinates = [];

    if (features && features.length > 0) {
      const geometry = features[0].geometry;
      if (geometry && geometry.coordinates.length > 0) {
        const coordinates = geometry.coordinates[0];

        for (let i = 0; i < coordinates.length; i++) {
          drawing_coordinates.push({
            latitude: coordinates[i][1],
            longitude: coordinates[i][0]
          });
        }
      }
    }

    if (drawing_coordinates.length > 2) {
      this.props.onDraw(drawing_coordinates);
    }
  }

  handleIdle = () => {

    const mapper = this.mapRef.current.getMap();
    if (!mapper.isStyleLoaded()) {
      setTimeout(() => {
        this.handleIdle();
      }, 500);
      return;
    }
    const { colors, properties_on_map_ids, active_property } = this.props;

    const borderHighlights = properties_on_map_ids && this.props.checkIfThereAreFilters() ? properties_on_map_ids : [];




    if (!mapper.getLayer('shapes_on_map')) {
      mapper.addLayer({
        id: 'shapes_on_map',
        type: 'fill',
        maxZoom: 19.99,
        source: 'parcelShapes',
        'source-layer': 'all_shapes',
        paint: {
          "fill-color": colors.border_color,
          "fill-outline-color": colors.actionable_text_color,
          "fill-opacity": this.props.main_map_location?.map_type === "satellite" ? 0.15 : 0.3,
        },
      });
    }

    // Safely get the active id if active_property is not null
    const activeId = active_property ? active_property.id : null;
    // TODO Switch back to only using id once the tiles cache seems to be cleared.
    const fillColorExpression = activeId ? [
      "case",
      // If the feature's id equals activeId and activeId is in borderHighlights, use colors.temp.
      [
        "all",
        [
          "any",
          ["==", ["get", "id"], ["literal", activeId]],
          ["==", ["get", "property_data_id"], ["literal", activeId]],
        ],
        ["in", ["literal", activeId], ["literal", borderHighlights]]
      ],
      colors.orange_color_muted,
      // Otherwise, if the feature's id is in borderHighlights, use colors.actionable_text_color.
      [
        "any",
        ["in", ["get", "id"], ["literal", borderHighlights]],
        ["in", ["get", "property_data_id"], ["literal", borderHighlights]]
      ],
      colors.orange_color,
      // Default.
      colors.border_color
    ] : [
      "case",
      [
        "any",
        ["in", ["get", "id"], ["literal", borderHighlights]],
        ["in", ["get", "property_data_id"], ["literal", borderHighlights]]
      ],
      colors.orange_color,
      colors.border_color
    ];

    const fillOutlineColorExpression = activeId ? [
      "case",
      [
        "all",
        [
          "any",
          ["==", ["get", "id"], ["literal", activeId]],
          ["==", ["get", "property_data_id"], ["literal", activeId]]
        ],
        ["in", ["literal", activeId], ["literal", borderHighlights]]
      ],
      colors.orange_color,
      [ // Nested case expression now properly wrapped in an array
        "case",
        [
          "any",
          ["in", ["get", "id"], ["literal", borderHighlights]],
          ["in", ["get", "property_data_id"], ["literal", borderHighlights]]
        ],
        colors.actionable_text_color,
        this.props.main_map_location?.map_type === "satellite" ? colors.white_color : colors.text_color
      ]
    ] : [
      "case",
      [
        "any",
        ["in", ["get", "id"], ["literal", borderHighlights]],
        ["in", ["get", "property_data_id"], ["literal", borderHighlights]]
      ],
      colors.actionable_text_color,
      this.props.main_map_location?.map_type === "satellite" ? colors.white_color : colors.text_color
    ];

    const fillOpacityExpression = [
      "case",
      [
        "any",
        ["in", ["get", "id"], ["literal", borderHighlights]],
        ["in", ["get", "property_data_id"], ["literal", borderHighlights]]
      ],
      0.3,
      this.props.main_map_location?.map_type === "satellite" ? 0.15 : 0.3
    ];

    mapper.setPaintProperty('shapes_on_map', 'fill-opacity', fillOpacityExpression);
    mapper.setPaintProperty('shapes_on_map', 'fill-color', fillColorExpression);
    mapper.setPaintProperty('shapes_on_map', 'fill-outline-color', fillOutlineColorExpression);
  };

  render() {
    const {
      user,
      scrollZoom = true,
      latitude,
      longitude,
      drawing,
      colors,
      dark_mode
    } = this.props;

    const { features } = this.state;

    const onDrawCreate = ({ features }) => {
      this.props.drawingCreated();
      this.sendCoordinates(features);
    };

    const onDrawUpdate = ({ features }) => {
      this.sendCoordinates(features);
    };





    const onClick = (evt) => {
      const mapper = this.mapRef.current.getMap();

      if (this.props.drawing && this.props.draw_mode === "circle") {
        const mapper = this.mapRef.current.getMap();

        const centerLng = evt.lngLat?.lng ?? mapper.unproject([evt.point.x, evt.point.y]).lng;
        const centerLat = evt.lngLat?.lat ?? mapper.unproject([evt.point.x, evt.point.y]).lat;

        const miles = Number(this.props.circle_radius_miles ?? 0.5);
        this.buildCircleAt({ lat: centerLat, lng: centerLng }, miles);
        this.fitMapToCircle({ lat: centerLat, lng: centerLng }, miles);
        const currentZoom = mapper.getZoom();
        this.props.updateCenter({
          latitude: centerLat,
          longitude: centerLng,
          isFromMapMovement: false
        });
        this.props.updateBounds(this.mapRef);

        return;
      }



      const clickPoint = evt.point;
      const zoom = mapper.getZoom();
      if (zoom < 11) {
        return;
      }

      let buffer = 5;
      const bbox = [
        [clickPoint.x - buffer, clickPoint.y - buffer],
        [clickPoint.x + buffer, clickPoint.y + buffer]
      ];

      let hitFeatures = mapper.queryRenderedFeatures(bbox, {
        layers: ['properties_on_map_circle']
      });

      let selectedIds = [];

      if (hitFeatures.length <= 0) {
        hitFeatures = mapper.queryRenderedFeatures(bbox, {
          layers: ['leads_on_map_symbol']
        });
      }

      if (hitFeatures.length > 0) {
        selectedIds = hitFeatures.map(f => f.properties.id);
      } else {
        /* TODO Maybe add back once parcel boundary done. :: const shapeHits = mapper.queryRenderedFeatures(bbox, {
          layers: ['shapes_on_map']
        }); */
        const shapeHits = [];
        if (shapeHits.length > 0) {
          selectedIds = shapeHits.map(f => f.properties.id);
        } else {
          // fallback to nearest
          const allCircles = mapper.queryRenderedFeatures({
            layers: ['properties_on_map_circle']
          });

          let minDist = Infinity, closestId = null;
          allCircles.forEach(f => {
            const [lon, lat] = f.geometry.coordinates;
            const pt = mapper.project([lon, lat]);
            const d = Math.hypot(pt.x - clickPoint.x, pt.y - clickPoint.y);
            if (d < minDist) {
              minDist = d;
              closestId = f.properties.id;
            }
          });
          if (closestId != null) selectedIds = [closestId];
        }
      }

      this.props.onMapClick(evt, selectedIds);
    };

    const getCenter = map => {
      if (map?.current) {
        map = map.current;
      }
      if (map) {
        if (map?.getCenter) {
          const coordinate = map?.getCenter();

          this.props.updateCenter({
            latitude: coordinate?.lat,
            longitude: coordinate?.lng,
            isFromMapMovement: true
          });
        }

        this.props.updateBounds(this.mapRef.current);
      }
    };

    const onStyleLoad = map => {
      this.props.onStyleLoad();
      getCenter(map);
    };

    const calculateZoom = map => {
      if (map) {
        const zoom = map;
        this.props.updateZoomProperties({
          zoom: zoom,
          isFromZoomEnd: true
        });

        this.props.updateBounds(this.mapRef);
      }
    };

    const {
      starting_zoom,
      maxZoom,
      minZoom,
      starting_lng,
      starting_lat,
      mapIsMoving,
      toggleMapMoving,
      hidePopover,
      map_type
    } = this.props;

    return (
      <MapGL
        ref={this.mapRef}
        mapboxAccessToken="pk.eyJ1IjoiZGVhbG1hY2hpbmUiLCJhIjoiY2pyczBqMXQ4MDRiMDQzcnhldmpzbnZsYyJ9.YGxxBHBvL31H6K1EBMuIog"
        mapStyle={
          map_type === "satellite"
            ? "mapbox://styles/mapbox/satellite-streets-v11?optimize=true"
            : dark_mode === "dark_mode"
              ? "mapbox://styles/dealmachine/cm47dri0x00zj01qqd9qodyt3?optimize=true"
              : "mapbox://styles/dealmachine/cm47adykc00ty01qr4v17bqmu?optimize=true"
        }
        initialViewState={{
          longitude: parseFloat(starting_lng),
          latitude: parseFloat(starting_lat),
          zoom: parseInt(starting_zoom)
        }}
        maxZoom={parseFloat(maxZoom)}
        minZoom={parseFloat(minZoom)}
        style={{
          width: "100%",
          height: "100%",
          position: "relative"
        }}
        onStyleData={(event) => {




        }}
        scrollZoom={scrollZoom}
        onZoomStart={() => {

          toggleMapMoving(true);
        }}
        onZoomEnd={event => {

          toggleMapMoving(false);
          calculateZoom(event.viewState.zoom);
        }}
        onMoveStart={() => {
          toggleMapMoving(true);
          clearTimeout(this._moveTimeout);

          hidePopover();
        }}
        onIdle={event => {
          toggleMapMoving(false);
        }}
        onMoveEnd={event => {
          toggleMapMoving(false);
          clearTimeout(this._moveTimeout);
          const map = this.mapRef;

          this._moveTimeout = setTimeout(() => {
            getCenter(map);
          }, 500);
        }}
        onLoad={event => {
          if (this.props.onRef) {
            this.props.onRef(this.mapRef.current);
          }

          const map = this.mapRef.current;
          onStyleLoad(map);
          const mapper = this.mapRef.current.getMap();

          mapper.addSource("parcelShapes", {
            type: "vector",
            maxzoom: 17,
            minzoom: 16,
            tiles: [
              "https://dj7nk1sdhkxvu.cloudfront.net/{z}/{x}/{y}.pbf"
            ],
          });

          const borderHighlights = this.props.borderHighlights || [];

          this.handleIdle();

          mapper.on('style.load', () => {
            // Your code here will run only once when the new style is done loading
            mapper.addSource("parcelShapes", {
              type: "vector",
              maxzoom: 17,
              minzoom: 16,
              tiles: [
                "https://dj7nk1sdhkxvu.cloudfront.net/{z}/{x}/{y}.pbf"
              ],
            });


          });


          map.on('styleimagemissing', () => {
            if (!mapper.hasImage('lead')) {
              mapper.loadImage(
                "/assets/images/active_pin.png",
                (error, image) => {
                  if (error) throw error;
                  if (!mapper.hasImage('lead'))
                    mapper.addImage('lead', image);
                }
              );
            }
            if (!mapper.hasImage('active_lead')) {
              mapper.loadImage(
                "/assets/images/orange_pin.png",
                (error, image) => {
                  if (error) throw error;
                  if (!mapper.hasImage('active_lead'))
                    mapper.addImage('active_lead', image);
                }
              );
            }
            if (!mapper.hasImage('lead_white')) {
              mapper.loadImage(
                "/assets/images/white_pin.png",
                (error, image) => {
                  if (error) throw error;
                  if (!mapper.hasImage('lead_white'))
                    mapper.addImage('lead_white', image);
                }
              );
            }
            if (!mapper.hasImage('person_pin')) {
              mapper.loadImage(
                "/assets/images/person_pin_orange.png",
                (error, image) => {
                  if (error) throw error;
                  if (!mapper.hasImage('person_pin'))
                    mapper.addImage('person_pin', image);
                }
              );
            }
          });


          mapper.loadImage("/assets/images/active_pin.png", (error, image) => {
            if (error) {
              console.log("ERROR ON LOADING IMAGE", error);
              return;
            }
            mapper.addImage("lead", image);
          });
          mapper.loadImage("/assets/images/white_pin.png", (error, image) => {
            if (error) return;
            mapper.addImage("lead_white", image);
          });
          mapper.loadImage("/assets/images/orange_pin.png", (error, image) => {
            if (error) return;
            mapper.addImage("active_lead", image);
          });
          mapper.loadImage(
            "/assets/images/person_pin_orange.png",
            (error, image) => {
              if (error) return;
              mapper.addImage("person_pin", image);
            }
          );
        }}
        onClick={onClick}
        logoPosition="bottom-left"
        attributionControl={false}
      >
        <Source
          id="locations_on_map"
          type="geojson"
          data={{
            type: "FeatureCollection",
            features: []
          }}
        >
          <Layer
            id="locations_on_map_fill"
            type="fill"
            paint={{
              "fill-color": colors.actionable_text_color,
              "fill-outline-color": colors.actionable_text_color,
              "fill-opacity": 0.05
            }}
          />
          <Layer
            id="locations_on_map_line"
            type="line"
            paint={{
              "line-color": colors.actionable_text_color,
              "line-width": 3,
              "line-opacity": 0.7
            }}
          />
        </Source>

        <Source
          id="routes_on_map"
          type="geojson"
          data={{
            type: "FeatureCollection",
            features: []
          }}
        >
          <Layer
            id="routes_on_map_line"
            type="line"
            minzoom={12}
            paint={{
              "line-color": [
                "match",
                ["get", "route_status"],
                "tier_1",
                colors.active_color,
                "tier_2",
                colors.orange_color_light,
                "tier_3",
                colors.error_color,
                "rgba(255, 255, 255, 0)"
              ],
              "line-width": 3,
              "line-opacity": 0.7
            }}
          />
        </Source>
        <Source
          id="active_route_on_map"
          type="geojson"
          data={{
            type: "FeatureCollection",
            features: []
          }}
        >
          <Layer
            id="active_route_on_map_line"
            type="line"
            minzoom={6}
            paint={{
              "line-color": colors.orange_color,
              "line-width": 3,
              "line-opacity": 1
            }}
          />
        </Source>

        <Source
          id="current_route_on_map"
          type="geojson"
          data={{
            type: "FeatureCollection",
            features: []
          }}
        >
          <Layer
            id="current_route_on_map_line"
            type="line"
            minzoom={6}
            paint={{
              "line-color": colors.orange_color,
              "line-width": 3,
              "line-opacity": 1
            }}
          />
        </Source>

        <Source
          id="properties_on_map"
          type="vector"
          tiles={['https://dbk6c6vs1pprr.cloudfront.net/{z}/{x}/{y}.pbf']}
          minzoom={11}
          maxzoom={17}
        >
          {this.props.checkIfThereAreFilters() && this.props.properties_on_map_ids.length > 0 ? (
            <Layer
              beforeId={"road-label"}
              id="properties_heatmap"
              type="heatmap"
              source-layer="all_properties"
              maxzoom={16}
              minzoom={11}
              filter={
                ["in", ["get", "id"], ["literal", this.props.properties_on_map_ids]]
              }
              paint={{
                // 1) How much each feature contributes to the heatmap (here uniform = 1)
                "heatmap-weight": .25,

                // 2) Intensify the blobs as you zoom in
                "heatmap-intensity": [
                  "interpolate", ["linear"], ["zoom"],
                  11, 0.5,
                  15, 2
                ],

                // 3) Color ramp from transparent → blue → cyan → lime → yellow → red
                "heatmap-color": [
                  "interpolate", ["linear"], ["heatmap-density"],
                  // low density: fully transparent
                  0, "rgba(242, 99, 58, 0)",
                  // build up through the lighter oranges
                  0.2, "#FFE5CC",
                  0.4, "#FFB366",
                  0.6, "#FF8C00",
                  0.8, "#F2633A",
                  1, "#f85224"
                ],

                // 4) Radius of influence grows as you zoom in
                "heatmap-radius": [
                  "interpolate", ["linear"], ["zoom"],
                  11, 20,
                  15, 30
                ],

                // 5) Fade out when you get really close (so circles take over)
                "heatmap-opacity": [
                  "interpolate", ["linear"], ["zoom"],
                  11, .8,
                  13, .7,
                  14, .6,
                  16, 0
                ]
              }}
            />) : null}
          <Layer
            id="properties_on_map_circle"
            type="circle"
            beforeId={"road-label"}
            source-layer={'all_properties'}
            minzoom={14}
            maxzoom={19.99}
            filter={
              ["!=", ["get", "id"], this.state.hoveredPropertyId]
            }
            paint={{
              "circle-color": [
                "case",
                this.props.checkIfThereAreFilters() ? ["in", ["get", "id"], ["literal", this.props.properties_on_map_ids]] : false,
                "rgba(242, 99, 58, 1)",
                ["in", ["get", "id"], ["literal", this.props.selectedIds]],
                "rgba(242, 99, 58, 1)",
                dark_mode === "dark_mode"
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(44, 44, 44, 0.75)"
              ],
              "circle-opacity": [
                "case",
                ["in", ["get", "id"], ["literal", this.props.leadData]],
                0,
                1
              ],
              "circle-radius": [
                "interpolate",
                ["exponential", 1.5],
                ["zoom"],
                11, 1,
                14, 3,
                17, 4
              ],
              "circle-radius-transition": { duration: 200, delay: 0 },
              "circle-stroke-width": [
                "match",
                ["get", "active_status"],
                "active_viewing", 1,
                "viewing", 1,
                "lead", 0,
                1
              ],
              "circle-stroke-color": [
                "case",
                ["in", ["get", "id"], ["literal", this.props.selectedIds]],
                "rgba(242, 99, 58, 1)",
                colors.card_color

              ]
            }}
          />

          <Layer
            id="properties_on_map_text"
            type="symbol"
            source-layer="all_properties"
            minzoom={16}
            beforeId={"road-label"}

            layout={{
              // Your existing text‐field cases unchanged...
              "text-field":
                this.props.main_map_location?.property_label_type === "property_address_range"
                  ? [
                    "case",
                    ["all", ["has", "parsed_range"], ["!=", ["get", "parsed_range"], ""]],
                    ["get", "parsed_range"],
                    ["get", "owner_lastname"]
                  ]
                  : this.props.main_map_location?.property_label_type === "property_address"
                    ? [
                      "case",
                      ["all", ["has", "property_address"], ["!=", ["get", "property_address"], ""]],
                      ["get", "property_address"],
                      ["get", "owner_lastname"]
                    ]
                    : this.props.main_map_location?.property_label_type === "equity_percent"
                      ? [
                        "case",
                        ["all", ["has", "equity_percent"], ["!=", ["get", "equity_percent"], ""]],
                        [
                          "concat",
                          ["to-string", ["round", ["*", ["get", "equity_percent"], 1]]],
                          "%"
                        ],
                        "--"
                      ]
                      : this.props.main_map_location?.property_label_type === "owner_lastname"
                        ? [
                          "case",
                          ["all", ["has", "parsed_range"], ["!=", ["get", "parsed_range"], ""]],
                          ["get", "parsed_range"],
                          ["get", "owner_lastname"]
                        ]
                        : ["get", "parsed_range"],
              "text-size": 12,
              "text-padding": 5,
              "text-anchor": "top",
              "text-justify": "center",
              "text-offset": [0, 0.5]
            }}

            paint={{
              // 1) Always white text
              "text-color": [
                "case",
                ["!=", this.props.main_map_location?.property_label_type, "equity_percent"],
                [
                  "case",
                  dark_mode === "dark_mode" || this.props.map_type === "satellite",
                  "#FFFFFF",
                  "#333333"
                ],
                ["<", ["get", "equity_percent"], 30], // equity < 30% → orange/red zone
                "#ffffff",                             // → white text
                "#333333"                              // else → dark gray
              ],                                        //

              // 2) Thick, blurred halo acting as a “background”
              "text-halo-width": 20,                                                 //
              "text-halo-blur": 20,                                                  //

              // 3) Halo color: red→yellow→green when equity_percent, else fallback
              "text-halo-color": [
                "case",                                                              //
                ["==", this.props.main_map_location?.property_label_type, "equity_percent"],
                [
                  "interpolate", ["linear"], ["get", "equity_percent"],             //
                  0, "rgba(255,   0,   0,   1)",  // 0% → red
                  50, "rgba(255, 255,   0,   1)",  // 50% → yellow
                  100, "rgba(  0, 255,   0,   1)"   // 100% → green
                ],
                // fallback halo color
                dark_mode === "dark_mode" || this.props.map_type === "satellite"
                  ? "rgba(0,   0,   0,  .4)"
                  : "rgba(255, 255, 255, .4)"
              ]
            }}
          />
          <Layer
            id="properties_on_map_circle_halo"
            type="circle"
            beforeId={"road-label"}
            source-layer="all_properties"
            filter={
              ["==", ["get", "id"], this.state.hoveredPropertyId]
            }
            paint={{
              "circle-radius": [
                "interpolate", ["exponential", 1.5], ["zoom"],
                11, 5,
                14, 10,
                17, 12
              ],
              "circle-color": "rgba(242, 99, 58, 0.3)",
              "circle-opacity": 0.7
            }}
          />
          <Layer
            id="properties_on_map_circle_hover"
            type="circle"
            beforeId={"road-label"}
            source-layer="all_properties"
            filter={
              ["==", ["get", "id"], this.state.hoveredPropertyId]
            }
            paint={{
              "circle-radius": [
                "interpolate", ["exponential", 1.5], ["zoom"],
                11, 3,
                14, 6,
                17, 8
              ],
              "circle-color": "rgba(242, 99, 58, 1)",
              "circle-opacity": 1,
              "circle-stroke-width": 1.5,
              "circle-stroke-color": "#ffffff"
            }}
          />
          <Layer
            id="leads_on_map_symbol"
            beforeId={"road-label"}
            type="circle"
            minzoom={11}
            source-layer={'all_properties'}
            filter={
              ["in", ["get", "id"], ["literal", this.props.leadData]]
            }
            paint={{
              'circle-color': '#31CCE5',
              "circle-stroke-color":
                [
                  "case",
                  ["in", ["get", "id"], ["literal", this.props.selectedIds]],
                  "rgba(242, 99, 58, 1)",
                  "#004F64"
                ],
              "circle-stroke-width": [
                "interpolate",
                ["exponential", 1.5],
                ["zoom"],
                11, .5,
                14, 1,
                17, 1.5
              ],
              "circle-radius":
                [
                  "interpolate",
                  ["exponential", 1.5],
                  ["zoom"],
                  11, 1,
                  14, 3,
                  17, 4
                ]
            }}
          />
        </Source>

        <Source
          id="street_view_marker"
          type="geojson"
          data={{
            type: "FeatureCollection",
            features: []
          }}
        >
          <Layer
            id="street_view_marker_symbol"
            type="symbol"
            minzoom={10}
            layout={{
              "icon-image": "person_pin",
              "icon-size": 0.5,
              "icon-anchor": "bottom"
            }}
          />
        </Source>
        {drawing && this.props.draw_mode ? (
          <DrawControl
            key={`${drawing}-${this.props.draw_mode}`}
            position="top-left"
            displayControlsDefault={false}
            defaultMode={this.props.draw_mode === "polygon" ? "draw_polygon" : "simple_select"}
            onCreate={onDrawCreate}
            onUpdate={onDrawUpdate}
            onDelete={() => this.props.onDraw([])}
            onInstance={(inst) => {
              this.drawControl = inst;
              this.props.onDrawRef?.(inst);
              const nextMode = this.props.draw_mode === "polygon" ? "draw_polygon" : "simple_select";
              try { inst.changeMode(nextMode); } catch { }
            }}
            styles={[
              {
                id: "gl-draw-line",
                type: "line",
                filter: [
                  "all",
                  ["==", "$type", "LineString"],
                  ["!=", "mode", "static"]
                ],
                layout: {
                  "line-cap": "round",
                  "line-join": "round"
                },
                paint: {
                  "line-color": colors.actionable_text_color,
                  "line-dasharray": [0.2, 2],
                  "line-width": 2
                }
              },
              // polygon fill
              {
                id: "gl-draw-polygon-fill",
                type: "fill",
                filter: [
                  "all",
                  ["==", "$type", "Polygon"],
                  ["!=", "mode", "static"]
                ],
                paint: {
                  "fill-color": colors.actionable_text_color,
                  "fill-outline-color": colors.actionable_text_color,
                  "fill-opacity": 0.1
                }
              },
              // polygon mid points
              {
                id: "gl-draw-polygon-midpoint",
                type: "circle",
                filter: [
                  "all",
                  ["==", "$type", "Point"],
                  ["==", "meta", "midpoint"]
                ],
                paint: {
                  "circle-radius": 3,
                  "circle-color": colors.actionable_text_color
                }
              },
              {
                id: "gl-draw-polygon-stroke-active",
                type: "line",
                filter: [
                  "all",
                  ["==", "$type", "Polygon"],
                  ["!=", "mode", "static"]
                ],
                layout: {
                  "line-cap": "round",
                  "line-join": "round"
                },
                paint: {
                  "line-color": colors.actionable_text_color,
                  "line-dasharray": [0.2, 2],
                  "line-width": 2
                }
              },
              // vertex point halos
              {
                id: "gl-draw-polygon-and-line-vertex-halo-active",
                type: "circle",
                filter: [
                  "all",
                  ["==", "meta", "vertex"],
                  ["==", "$type", "Point"],
                  ["!=", "mode", "static"]
                ],
                paint: {
                  "circle-radius": 5,
                  "circle-color": colors.card_color
                }
              },
              // vertex points
              {
                id: "gl-draw-polygon-and-line-vertex-active",
                type: "circle",
                filter: [
                  "all",
                  ["==", "meta", "vertex"],
                  ["==", "$type", "Point"],
                  ["!=", "mode", "static"]
                ],
                paint: {
                  "circle-radius": 3,
                  "circle-color": colors.actionable_text_color
                }
              },

              // INACTIVE (static, already drawn)
              // line stroke
              {
                id: "gl-draw-line-static",
                type: "line",
                filter: [
                  "all",
                  ["==", "$type", "LineString"],
                  ["==", "mode", "static"]
                ],
                layout: {
                  "line-cap": "round",
                  "line-join": "round"
                },
                paint: {
                  "line-color": colors.actionable_text_color,
                  "line-width": 3
                }
              },
              // polygon fill
              {
                id: "gl-draw-polygon-fill-static",
                type: "fill",
                filter: [
                  "all",
                  ["==", "$type", "Polygon"],
                  ["==", "mode", "static"]
                ],
                paint: {
                  "fill-color": colors.actionable_text_color,
                  "fill-outline-color": colors.actionable_text_color,
                  "fill-opacity": 0.1
                }
              },
              // polygon outline
              {
                id: "gl-draw-polygon-stroke-static",
                type: "line",
                filter: [
                  "all",
                  ["==", "$type", "Polygon"],
                  ["==", "mode", "static"]
                ],
                layout: {
                  "line-cap": "round",
                  "line-join": "round"
                },
                paint: {
                  "line-color": colors.actionable_text_color,
                  "line-width": 2
                }
              }
            ]}
          />
        ) : null}
      </MapGL>
    );
  }
}

export default Map;